import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import { Form, Input, Button, DatePicker, Table, Spin, Pagination } from "antd";
import API, { PLAY_HIS, PLAY_HIS_EXCEL } from "../../network/api";
import moment from "moment";
import "moment-timezone";
import { TEXT_DEF } from "../../constant/text";

const TEXT = {
  totalTurn: "Số lượt đã nhận",
  usedTurn: "Số lượt đã chơi",
  turnRemaining: "Số lượt còn lại",
  time: "Thời gian",
};

/**
 * @typedef {Object} Row
 * @property {any} key
 * @property {number} [stt]
 * @property {string} username
 * @property {string} createdAt
 * @property {number} totalTurns
 * @property {number} usedTurns
 * @property {number} turnRemaining
 */

/**
 * @typedef {Object} Summary
 * @property {number} totalReceived
 * @property {number} totalUsedTurns
 * @property {number} totalRemainTurns
 */

const PlayHistoryList = () => {
  const [form] = Form.useForm();
  const [, forceUpdate] = useState({});
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [dataList, setDataList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dataFilter, setDataFilter] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [summary, setSummary] = useState({
    totalReceived: 0,
    totalUsedTurns: 0,
    totalRemainTurns: 0,
  });

  const locale = { emptyText: TEXT_DEF.text0035 };

  useEffect(() => {
    forceUpdate({});
    const usernameFilterLocal = localStorage.getItem("usernameFilter");
    if (usernameFilterLocal) {
      setDataFilter(`username=${usernameFilterLocal}`);
      form.setFieldValue("usernameFilter", usernameFilterLocal);
      localStorage.removeItem("usernameFilter");
    }
  }, [form]);

  useEffect(() => {
    getListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dataFilter, sortField, sortOrder]);

  const buildQuery = (pageNum) => {
    const qs = [`pageSize=10`, `page=${pageNum ?? page}`];
    if (dataFilter && dataFilter.trim() !== "") qs.push(dataFilter);
    if (sortField && sortOrder) {
      const order = sortOrder === 'ascend' ? 'ASC' : 'DESC';
      qs.push(`sortField=${sortField}&sortOrder=${order}`);
      console.log('buildQuery with sort:', sortField, order);
    }
    const queryString = qs.join("&");
    console.log('Final query:', queryString);
    return queryString;
  };

  const getListData = async () => {
    setLoading(true);
    try {
      const resp = await API.get(`${PLAY_HIS}?${buildQuery()}`);
      const payload = resp?.data?.data;

      const list = Array.isArray(payload?.data) ? payload.data : [];
      const mapped = list.map((item) => ({
        key: item?.id,
        stt: item?.stt,
        username: item?.username,
        createdAt: item?.createdAt,
        totalTurns: Number(item?.totalTurns ?? 0),
        usedTurns: Number(item?.usedTurns ?? 0),
        turnRemaining: Number(item?.remainTurns ?? 0),
      }));

      setDataList(mapped);
      setTotalRecord(Number(payload?.total ?? mapped.length) || 0);

      // LẤY THEO SUMMARY từ API (ưu tiên)
      const s = payload?.summary ?? {};
      const fromApi = {
        // chấp nhận nhiều tên khoá để đề phòng backend đặt khác
        totalReceived:
          Number(s.totalReceived ?? s.totalTurns ?? s.total_gained ?? 0) || 0,
        totalUsedTurns: Number(s.totalUsedTurns ?? s.totalPlayed ?? s.total_uses ?? 0) || 0,
        totalRemainTurns:
          Number(s.totalRemainTurns ?? s.totalRemaining ?? s.remain ?? 0) || 0,
      };

      if (fromApi.totalReceived || fromApi.totalUsedTurns || fromApi.totalRemainTurns) {
        setSummary(fromApi);
      } else {
        // Fallback: tự cộng từ list TRANG HIỆN TẠI
        const totals = mapped.reduce(
          (acc, cur) => {
            acc.totalReceived += cur.totalTurns || 0;
            acc.totalUsedTurns += cur.usedTurns || 0;
            acc.totalRemainTurns += cur.turnRemaining || 0;
            return acc;
          },
          { totalReceived: 0, totalUsedTurns: 0, totalRemainTurns: 0 }
        );
        setSummary(totals);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onChangePage = (p) => setPage(p);
  const showTotal = (total) => `Tổng cộng ${total} dữ liệu`;

  const handleTableChange = (pagination, filters, sorter) => {
    console.log('handleTableChange sorter:', sorter);
    // Ant Design Table có thể trả về sorter là object hoặc array
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    
    if (currentSorter) {
      // Ưu tiên lấy field từ sorter.field, sau đó columnKey, rồi mới đến column.dataIndex/column.key
      const fieldName = currentSorter.field || currentSorter.columnKey || (currentSorter.column && (currentSorter.column.dataIndex || currentSorter.column.key));
      const order = currentSorter.order;
      
      console.log('Sort field name:', fieldName, 'order:', order);
      
      if (fieldName && order) {
        setSortField(fieldName);
        setSortOrder(order);
        setPage(1); // Reset về trang đầu khi sort
      } else {
        // Clear sort khi click lần nữa (order là null/undefined)
        setSortField(null);
        setSortOrder(null);
      }
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const onSearch = () => {
    let params = "";
    const usernameVal = form.getFieldValue("usernameFilter");
    const statusVal = form.getFieldValue("userStatus");

    if (usernameVal)
      params += `username=${String(usernameVal).trim().toLowerCase()}`;
    if (statusVal)
      params += params !== "" ? `&status=${statusVal}` : `status=${statusVal}`;
    if (dateFrom)
      params += params !== "" ? `&fromDate=${dateFrom}` : `fromDate=${dateFrom}`;
    if (dateTo) {
      params += params !== "" ? `&toDate=${dateTo}` : `toDate=${dateTo}`;
      if (!dateFrom)
        params += `&fromDate=${moment("2025-07-15").format("YYYY-MM-DD")}`;
    }

    setPage(1);
    setDataFilter(params !== "" ? params : null);
  };

  const dataColums = [
    { title: "STT", dataIndex: "stt", key: "stt" },
    { title: TEXT_DEF.text0002, dataIndex: "username", key: "username" },
    {
      title: TEXT.time,
      key: "createdAt",
      dataIndex: "createdAt",
      render: (value) =>
        moment(value).format("DD-MM-YYYY HH:mm:ss"),
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: TEXT.totalTurn,
      key: "totalTurns",
      dataIndex: "totalTurns",
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: TEXT.usedTurn,
      key: "usedTurns",
      dataIndex: "usedTurns",
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: TEXT.turnRemaining,
      key: "turnRemaining",
      dataIndex: "turnRemaining",
    },
  ];

  const onChangeDate = (e, type) => {
    if (e) {
      const d = moment(e.$d);
      if (type === "from") setDateFrom(d.startOf("day").format("YYYY-MM-DD"));
      else setDateTo(d.endOf("day").format("YYYY-MM-DD"));
    } else {
      if (type === "from") setDateFrom(null);
      else setDateTo(null);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const qs = dataFilter && dataFilter.trim() !== "" ? `?${dataFilter}` : "";
      const response = await API.post(
        `${PLAY_HIS_EXCEL}${qs}`,
        {},
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const disposition = response.headers?.["content-disposition"];
      let fileName = "lich_su_luot_choi.xlsx";
      if (disposition && disposition.includes("filename=")) {
        fileName = disposition.split("filename=")[1].replace(/"/g, "");
      }
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const renderHistory = () => (
    <>
      <div className="col-12">
        <Form form={form} layout="inline" autoComplete="off" onFinish={onSearch}>
          <div className="col-12" style={{ marginBottom: 15, display: "flex" }}>
            <div className="col-5">
              <Form.Item name="usernameFilter" label={TEXT_DEF.text0002}>
                <Input style={{ width: "100%" }} />
              </Form.Item>
            </div>
          </div>

          <div className="col-12" style={{ marginBottom: 15, display: "flex" }}>
            <div className="col-5">
              <Form.Item name="dateFrom" label={TEXT_DEF.text0032}>
                <DatePicker
                  placeholder=""
                  onChange={(e) => onChangeDate(e, "from")}
                  format={"DD-MM-YYYY"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
            <div className="col-5">
              <Form.Item name="dateTo" label={TEXT_DEF.text0033}>
                <DatePicker
                  placeholder=""
                  onChange={(e) => onChangeDate(e, "to")}
                  format={"DD-MM-YYYY"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
          </div>

          <div className="payment-search">
            <Button htmlType="submit" type="primary">
              {TEXT_DEF.text0031}
            </Button>
            <Button
              style={{ marginLeft: 20 }}
              onClick={handleDownloadExcel}
              htmlType="button"
              type="primary"
            >
              {TEXT_DEF.text0050}
            </Button>
          </div>
        </Form>
      </div>

      {/* 3 tổng số — NGAY SAU Ô SEARCH & TRƯỚC BẢNG, lấy từ summary API */}
      <div
        className="col-12"
        style={{
          display: "flex",
          gap: 40,
          margin: "12px 0 8px",
          fontWeight: 600,
          fontSize: 15,
          flexWrap: "wrap",
        }}
      >
        <div>Tổng số lượt đã nhận: {summary.totalReceived}</div>
        <div>Tổng số lượt đã chơi: {summary.totalUsedTurns}</div>
        <div>Tổng số lượt còn lại: {summary.totalRemainTurns}</div>
      </div>

      <Spin spinning={loading} delay={500} size="large">
        <div style={{ display: "flex", width: "100%", marginTop: 10 }}>
          <Table
            key="conversionList"
            locale={locale}
            columns={dataColums}
            dataSource={dataList}
            pagination={false}
            bordered
            style={{ width: "100%" }}
            onChange={handleTableChange}
          />
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            marginTop: 15,
            justifyContent: "flex-end",
          }}
        >
          <Pagination
            current={page}
            total={totalRecord}
            onChange={onChangePage}
            defaultPageSize={10}
            showSizeChanger={false}
            showTotal={showTotal}
          />
        </div>
      </Spin>
    </>
  );

  return <div className="col-12">{renderHistory()}</div>;
};

export default PlayHistoryList;
